import * as R from 'ramda';

import { Client, CLIENTS, Tenant } from '../../utilities/featureToggleData';

export { Client, CLIENTS, Tenant };

type ClientType = (typeof Client)[keyof typeof Client];
type TenantType = (typeof Tenant)[keyof typeof Tenant];

const subDomain: string = window.location.hostname.split('.')[0];
const environment: string = window.location.hostname.split('.')[1];

export enum Environment {
  LOCAL = 'local-atom',
  DEV = 'dev-atom',
  QA = 'qa-atom',
  UAT = 'uat-atom',
  PRODUCTION = 'atomapp',
}

// EMULATION SETTINGS
// Certain features in Atom only work for certain data sets. For local development, this
// means that we need to be able to "fake" what client local is considered. The following
// constants can be changed accordingly to produce this effect for their given feature.

// Multi-location setting --> Client.UDOT or Client.ALDOT
export const MAL_EMULATION_CLIENT = Client.UDOT;

// Asset requests setting --> Client.SDDOT, Client.ARDOT, or Client.ADOT
export const ASSET_REQUEST_EMULATION_CLIENT = Client.ADOT;

// Gets current client. If current client is Client.LOCAL and an emulation client is passed in, use the emualtion client instead
export const getCurrentClient = (emulationClient?: ClientType): ClientType => {
  const currentClient = R.keys(CLIENTS).find(client =>
    R.includes(subDomain, CLIENTS[client]),
  );

  return currentClient === Client.LOCAL && emulationClient
    ? emulationClient
    : currentClient;
};

// Returns true if the current environment is in the passed in list of environments
export const isCurrentEnvironment = (environments: Environment[]): boolean => {
  return R.includes(environment, [...environments, Environment.LOCAL]);
};

// Returns true if the current tenant is in the passed in list of tenants
export const isCurrentTenant = (tenants: TenantType[]): boolean => {
  return R.includes(subDomain, [...tenants, Tenant.LOCAL]);
};

// Returns true if the current tenant is in the passed in client grouping
// ex Client.SADA will return true for dev-sada, qa-sada, uat-sada, and sada tenants
// excludeLocal boolean can be used to take local out of the check
export const isCurrentClient = (
  clients: ClientType[],
  excludeLocal?: boolean,
): boolean => {
  const tenantsList = clients.reduce((acc, client) => {
    return [...acc, ...CLIENTS[client]];
  }, []);

  const local = !!excludeLocal ? [] : [Tenant.LOCAL];
  return R.includes(subDomain, [...tenantsList, ...local]);
};

// Shows test-environment branding in DEV, QA, UAT, and local.
export const showTestEnvironmentLabel = isCurrentEnvironment([
  Environment.DEV,
  Environment.QA,
  Environment.UAT,
]);

// ── DEV WORK ──

// Gates the work-list policy resource to dev-sada and local.
export const showWorkListPolicyResource = isCurrentTenant([Tenant.DEV_SADA]);

// Returns true if the current environment is DEV or QA
// Use to help keep features out of UAT and PROD that are under development
export const showDevelopmentFeature = isCurrentEnvironment([
  Environment.DEV,
  Environment.QA,
]);

// Gates ADOT inventory requests (replenishments, transfers, adjustments)
export const showAdotInventoryRequests = isCurrentEnvironment([
  Environment.DEV,
  Environment.QA,
]);
